import { Song } from "../types/song";

export type SongLibraryValidation =
  | {
      ok: true;
      songs: Song[];
    }
  | {
      ok: false;
      error: string;
    };

const msInDay = 86400000;
const startDate = new Date(2022, 3, 15);

function getLocalMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getLocalDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function getTodaysSolution(
  library: Song[],
  date: Date = new Date()
): Song {
  const dayIndex = Math.floor(
    (getLocalMidnight(date).getTime() - startDate.getTime()) / msInDay
  );
  const index = ((dayIndex % library.length) + library.length) % library.length;

  return library[index];
}

export function getGameKey(song: Song, date: Date = new Date()): string {
  return `${getLocalDateKey(date)}:${song.youtubeId}`;
}

export function validateSongLibrary(input: unknown): SongLibraryValidation {
  if (!Array.isArray(input) || input.length === 0) {
    return {
      ok: false,
      error: "Add at least one song.",
    };
  }

  const youtubeIds = new Set<string>();
  const songs: Song[] = [];

  for (let index = 0; index < input.length; index += 1) {
    const value = input[index];
    const songNumber = index + 1;
    const candidate = value as Partial<Song>;
    const artist =
      typeof candidate.artist === "string" ? candidate.artist.trim() : "";
    const name = typeof candidate.name === "string" ? candidate.name.trim() : "";
    const youtubeId =
      typeof candidate.youtubeId === "string"
        ? candidate.youtubeId.trim()
        : "";
    const startSeconds =
      typeof candidate.startSeconds === "number"
        ? candidate.startSeconds
        : undefined;

    if (!artist) {
      return {
        ok: false,
        error: `Song ${songNumber} is missing an artist.`,
      };
    }

    if (!name) {
      return {
        ok: false,
        error: `Song ${songNumber} is missing a name.`,
      };
    }

    if (!youtubeId) {
      return {
        ok: false,
        error: `Song ${songNumber} is missing a YouTube ID.`,
      };
    }

    if (youtubeIds.has(youtubeId)) {
      return {
        ok: false,
        error: `YouTube ID ${youtubeId} appears more than once.`,
      };
    }

    if (
      startSeconds !== undefined &&
      (!Number.isFinite(startSeconds) || startSeconds < 0)
    ) {
      return {
        ok: false,
        error: `Song ${songNumber} has an invalid start time.`,
      };
    }

    youtubeIds.add(youtubeId);
    songs.push({
      artist,
      name,
      youtubeId,
      ...(startSeconds === undefined
        ? {}
        : { startSeconds: Math.floor(startSeconds) }),
    });
  }

  return {
    ok: true,
    songs,
  };
}
